// محمّل جداول العجلة المزدوجة (شخصيات + أسلحة).
// كل سحبة لها سعران: اعتيادي (regularCosts[i]) ومتقدم (advancedCosts[i]).
import charactersData from "./dual-characters.json";
import weaponsData from "./dual-weapons.json";

export const DUAL_TYPES = {
  characters: charactersData,
  weapons: weaponsData,
};

export function validateDualPricing() {
  const errors = [];
  Object.entries(DUAL_TYPES).forEach(([typeKey, data]) => {
    Object.entries(data.regions).forEach(([regKey, reg]) => {
      if (!Array.isArray(reg.regularCosts) || reg.regularCosts.length !== data.totalDraws) {
        errors.push(`[dual/${typeKey}/${regKey}] regularCosts length !== totalDraws`);
      }
      if (!Array.isArray(reg.advancedCosts) || reg.advancedCosts.length !== data.totalDraws) {
        errors.push(`[dual/${typeKey}/${regKey}] advancedCosts length !== totalDraws`);
      }
      const sumReg = (reg.regularCosts || []).reduce((s, c) => s + c, 0);
      if (sumReg !== reg.totalRegular) {
        errors.push(`[dual/${typeKey}/${regKey}] sum regular ${sumReg} !== totalRegular ${reg.totalRegular}`);
      }
      const sumAdv = (reg.advancedCosts || []).reduce((s, c) => s + c, 0);
      if (sumAdv !== reg.totalAdvanced) {
        errors.push(`[dual/${typeKey}/${regKey}] sum advanced ${sumAdv} !== totalAdvanced ${reg.totalAdvanced}`);
      }
    });
  });
  return errors;
}

if (typeof window !== "undefined" && process.env.NODE_ENV !== "production") {
  const errs = validateDualPricing();
  if (errs.length > 0) {
    console.error("[DUAL PRICING VALIDATION FAILED]\n" + errs.join("\n"));
  } else {
    console.log("[DUAL PRICING] ✅ Loaded — regularCosts + advancedCosts");
  }
}

// يطبّق أسعار السحبات الحالية القادمة من الخادم (بعد أي تصحيح مؤكد) على الجداول المحلية.
const API_KEYS = { characters: "dual_characters", weapons: "dual_weapons" };
export function applyDualPrices(data) {
  let changed = false;
  Object.entries(API_KEYS).forEach(([typeKey, apiKey]) => {
    const cfg = DUAL_TYPES[typeKey];
    Object.entries(data?.[apiKey] || {}).forEach(([regKey, p]) => {
      const reg = cfg.regions[regKey];
      const ok = (arr) => Array.isArray(arr) && arr.length === cfg.totalDraws && arr.every((v) => Number.isFinite(v) && v > 0);
      if (!reg || !ok(p.regularCosts) || !ok(p.advancedCosts)) return;
      if (JSON.stringify(reg.regularCosts) === JSON.stringify(p.regularCosts) && JSON.stringify(reg.advancedCosts) === JSON.stringify(p.advancedCosts)) return;
      reg.regularCosts = [...p.regularCosts];
      reg.advancedCosts = [...p.advancedCosts];
      reg.totalRegular = p.regularCosts.reduce((s, c) => s + c, 0);
      reg.totalAdvanced = p.advancedCosts.reduce((s, c) => s + c, 0);
      changed = true;
    });
  });
  return changed;
}

// دوال مساعدة
export function getDualConfig(type) {
  return DUAL_TYPES[type] || null;
}

export function getDualRegion(type, region) {
  const cfg = getDualConfig(type);
  if (!cfg) return null;
  return cfg.regions[region] || null;
}

export const DUAL_ACCOUNT_TYPES = Object.entries(
  DUAL_TYPES.characters.regions
).map(([id, r]) => ({
  id,
  label: r.regionLabel,
  subtitle: r.regionSubtitle,
}));

/**
 * حساب النتيجة حسب حالة كل سحبة.
 * drawStates: array لكل سحبة: { selection: null | "regular" | "advanced" | "pulled" }
 * discountPct: 0..100 يُطبَّق على تكلفة السحبات المُختارة قبل خصم الرصيد.
 *
 * قواعد:
 *   - selection = null      → لا تدخل في الحساب (السحبة غير محددة)
 *   - selection = "pulled"  → تكلفة = 0 (مسحوبة مسبقًا)
 *   - selection = "regular" → تكلفة = regularCosts[i]
 *   - selection = "advanced"→ تكلفة = advancedCosts[i]
 *   - discount مطبَّق per-draw ثم يُطرح balance.
 */
export function calculateDualResult(type, region, drawStates, currentCpBalance, discountPct = 0) {
  const reg = getDualRegion(type, region);
  if (!reg) return null;

  const totalDraws = DUAL_TYPES[type].totalDraws;
  const regularCosts = reg.regularCosts || [];
  const advancedCosts = reg.advancedCosts || [];
  const disc = Math.max(0, Math.min(Number(discountPct) || 0, 100)) / 100;
  const balance = Math.max(0, Number(currentCpBalance) || 0);

  const active = Array.from({ length: totalDraws }, (_, i) => {
    const s = (drawStates && drawStates[i]) || { selection: null };
    const selection = s.selection || null;
    let base = 0;
    if (selection === "regular") base = regularCosts[i] || 0;
    else if (selection === "advanced") base = advancedCosts[i] || 0;
    const effectiveCost = Math.round(base * (1 - disc));
    return {
      draw: i + 1,
      regularCost: regularCosts[i] || 0,
      advancedCost: advancedCosts[i] || 0,
      selection,
      baseCost: base,
      effectiveCost,
    };
  });

  const remainingDrawCost = active.reduce((s, d) => s + d.effectiveCost, 0);
  const requiredCpToBuy = Math.max(remainingDrawCost - balance, 0);
  const balanceCoversAll = remainingDrawCost > 0 && balance >= remainingDrawCost;

  const selectedCount = active.filter((d) => d.selection !== null).length;
  const pulledCount = active.filter((d) => d.selection === "pulled").length;
  const regularCount = active.filter((d) => d.selection === "regular").length;
  const advancedCount = active.filter((d) => d.selection === "advanced").length;
  const unselectedCount = active.filter((d) => d.selection === null).length;

  let running = 0;
  const breakdown = active.map((d) => {
    running += d.effectiveCost;
    return { ...d, cost: d.baseCost, cumulative: running };
  });

  // reference: total of all-regular plan
  const totalPlan = reg.totalRegular;

  return {
    active,
    breakdown,
    totalPlan,
    totalRegular: reg.totalRegular,
    totalAdvanced: reg.totalAdvanced,
    remainingDrawCost,
    balance,
    discountPct: disc * 100,
    requiredCpToBuy,
    balanceCoversAll,
    selectedCount,
    pulledCount,
    regularCount,
    advancedCount,
    unselectedCount,
    itemsInfo: DUAL_TYPES[type].itemsInfo,
  };
}
